import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LogoIcon } from '../components/Logo';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  BarChart3,
  User,
  LogOut,
  Menu,
  ChevronRight,
  Target,
  Upload,
} from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/sales', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/sales/leads', label: 'All Leads', icon: Users },
  { path: '/sales/leads/new', label: 'Add Lead', icon: UserPlus },
  { path: '/sales/leads/upload', label: 'Bulk Upload', icon: Upload },
];

function Sidebar({ className }) {
  const location = useLocation();

  return (
    <nav className={cn("space-y-1", className)}>
      {navItems.map((item) => {
        const isActive = item.exact 
          ? location.pathname === item.path
          : location.pathname.startsWith(item.path);
        return (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
              isActive
                ? "bg-indigo-50 text-indigo-700"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            )}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
            {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        );
      })}
    </nav>
  );
}

export default function SalesLayout() {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/sales/login', { replace: true });
      } else if (!['admin', 'sales'].includes(user?.role)) {
        // Non-sales/admin users should not access sales portal
        navigate('/dashboard', { replace: true });
      }
    }
  }, [loading, isAuthenticated, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !['admin', 'sales'].includes(user?.role)) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sales-layout">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 z-40 h-16 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <div className="p-6 border-b border-slate-200">
                  <Link to="/sales" className="flex items-center gap-3">
                    <LogoIcon className="w-10 h-10" />
                    <div>
                      <span className="text-xl font-jakarta font-bold bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">Desqly</span>
                      <span className="block text-xs text-slate-500">Sales Portal</span>
                    </div>
                  </Link>
                </div>
                <div className="p-4">
                  <Sidebar />
                </div>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/sales" className="flex items-center gap-3">
              <LogoIcon className="w-10 h-10" />
              <div className="hidden sm:block">
                <span className="text-xl font-jakarta font-bold bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">Desqly</span>
                <span className="block text-xs text-slate-500">Sales Portal</span>
              </div>
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {user?.role === 'admin' && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Admin Portal
                </Link>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-700">
                      {user?.name?.charAt(0) || 'S'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs text-slate-500">
                  {user?.role === 'admin' ? 'Administrator' : 'Sales Team'}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-slate-200 flex-col">
        <div className="flex-1 p-4 overflow-y-auto">
          <Sidebar />
        </div>
        <div className="p-4 border-t border-slate-200">
          <div className="p-4 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl">
            <div className="flex items-center gap-2 text-indigo-700 mb-2">
              <Target className="w-4 h-4" />
              <span className="text-sm font-medium">Sales Tip</span>
            </div>
            <p className="text-xs text-slate-600">
              Follow up with leads within 24 hours to increase conversion rates by up to 60%.
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 pt-16 min-h-screen flex flex-col">
        <div className="flex-1 p-6 lg:p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
